import type { ReactNode } from 'react'
import { Button } from '@/components/Button'
import { Card, CardContent, CardHeader } from '@/components/Card'
import { FileButton } from '@/components/FileButton'
import { Icon, type IconName } from '@/components/Icon'
import styles from './TransferCard.module.css'

export interface TransferCardProps {
  isExportPending: boolean
  isImportPending: boolean
  isExportDisabled: boolean
  isImportDisabled: boolean
  onExport: () => unknown
  onImport: (files: FileList) => unknown
  testId?: string
}

/**
 * Exports the selected version to a YAML file, or imports one over the draft.
 */
export function TransferCard({
  isExportPending,
  isImportPending,
  isExportDisabled,
  isImportDisabled,
  onExport,
  onImport,
  testId,
}: TransferCardProps) {
  return (
    <Card testId={testId}>
      <CardHeader>
        <h2 className={styles.cardTitle}>Import / export</h2>
      </CardHeader>
      <CardContent>
        <p className={styles.description}>
          Here you can import and export the currently selected legend version.
        </p>
        <div className={styles.items}>
          <TransferItem
            iconName="download"
            title="Export"
            action={
              <Button
                label="Export"
                variant="primary"
                isPending={isExportPending}
                isDisabled={isExportDisabled}
                onPress={onExport}
                testId={testId && `${testId}-export`}
              />
            }
          >
            This will export the <strong>currently selected version</strong>{' '}
            (including draft) to a YAML file.
          </TransferItem>
          <TransferItem
            iconName="upload"
            title="Import"
            action={
              <FileButton
                accept=".yaml,.yml"
                isDisabled={isImportDisabled}
                onChange={onImport}
              >
                <Button
                  label="Import"
                  isPending={isImportPending}
                  isDisabled={isImportDisabled}
                  testId={testId && `${testId}-import`}
                />
              </FileButton>
            }
          >
            This will replace your <strong>current draft</strong> with what is
            contained in the file. If there is no current draft, a new one is
            created.
          </TransferItem>
        </div>
      </CardContent>
    </Card>
  )
}

interface TransferItemProps {
  iconName: IconName
  title: string
  action: ReactNode
  children: ReactNode
}

function TransferItem({
  iconName,
  title,
  action,
  children,
}: TransferItemProps) {
  return (
    <div className={styles.item}>
      <span className={styles.icon}>
        <Icon name={iconName} size={18} />
      </span>
      <div className={styles.content}>
        <p className={styles.title}>{title}</p>
        <p className={styles.itemDescription}>{children}</p>
      </div>
      <div className={styles.action}>{action}</div>
    </div>
  )
}
